import { defineMessages, useIntl } from 'react-intl';
import { UserCirclePlus } from '@phosphor-icons/react';
import { Button, Dialog, IconBadge } from '../../design-system/ui';
import { useAuth } from '../../hooks/useAuth';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { closeSignInPrompt } from '../../redux/features/authPrompt/authPromptSlice';
import { useEffect, useCallback } from 'react';
import { markSignInSaveNudgeDismissedThisSession } from '../../utils/engagement/sign-in-save-nudge-session';
import { trackEvent } from '../../utils/analytics/analytics';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';

const messages = defineMessages({
  title: {
    id: 'auth.signInPrompt.title',
    defaultMessage: 'Sign in to save jobs',
    description: 'Title for sign-in prompt modal encouraging users to sign in to save jobs',
  },
  description: {
    id: 'auth.signInPrompt.description',
    defaultMessage: 'Activate your account to save jobs, sync progress, and pick up your search on any device.',
    description: 'Description text in sign-in prompt modal explaining benefits of activating an account',
  },
  primaryAction: {
    id: 'auth.signInPrompt.primaryAction',
    defaultMessage: 'Activate account',
    description: 'Primary button label in sign-in prompt modal to activate an account immediately',
  },
  secondaryAction: {
    id: 'auth.signInPrompt.secondaryAction',
    defaultMessage: 'Maybe later',
    description: 'Secondary button label in sign-in prompt modal to dismiss the prompt',
  },
  closeLabel: {
    id: 'auth.signInPrompt.closeLabel',
    defaultMessage: 'Close',
    description: 'Accessibility label for close button in sign-in prompt modal',
  },
});

export function SignInPromptModal() {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { isAuthenticated, signIn } = useAuth();
  const isOpen = useAppSelector((state) => state.authPrompt.isSignInPromptOpen);
  const dispatch = useAppDispatch();

  const handleCloseOnly = useCallback(() => {
    dispatch(closeSignInPrompt());
  }, [dispatch]);

  const handleDismissWithoutSignIn = useCallback(() => {
    markSignInSaveNudgeDismissedThisSession();
    trackEvent({
      name: 'auth_interaction',
      params: { action: 'sign_in_prompt_dismiss', surface: 'sign_in_prompt_modal', locale: intl.locale },
    });
    dispatch(closeSignInPrompt());
  }, [dispatch, intl.locale]);

  useEffect(() => {
    if (isAuthenticated && isOpen) {
      dispatch(closeSignInPrompt());
    }
  }, [dispatch, isAuthenticated, isOpen]);

  // Fires once per open — this only reaches a signed-out visitor (the effect above closes the
  // modal immediately if they're authenticated), so it's the "login-wall friction" moment.
  useEffect(() => {
    if (isOpen) {
      trackEvent({
        name: 'sign_in_prompt_shown',
        params: { surface: 'sign_in_prompt_modal', locale: intl.locale },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once per open transition, not on every intl.locale change
  }, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleDismissWithoutSignIn}
      closeLabel={formatMessage(messages.closeLabel)}
      title={<Msg {...messages.title} />}
      actions={
        <>
          <Button variant="secondary" onClick={handleDismissWithoutSignIn}>
            <Msg {...messages.secondaryAction} />
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              trackEvent({
                name: 'auth_interaction',
                params: { action: 'sign_in_click', surface: 'sign_in_prompt_modal', locale: intl.locale },
              });
              handleCloseOnly();
              signIn(undefined, { screenHint: 'signup' });
            }}
          >
            <Msg {...messages.primaryAction} />
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <IconBadge icon={UserCirclePlus} size="lg" shape="square" />
        <p className="type-body text-jc-ink-secondary">
          <Msg {...messages.description} />
        </p>
      </div>
    </Dialog>
  );
}
